const PORTFOLIOS_KEY = 'portfolios';
const ACTIVE_PORTFOLIO_KEY = 'active_portfolio';

export interface PortfolioInfo {
  id: string;
  name: string;
}

export interface Order {
  id: string;
  symbol: string;
  shares: number;
  pricePerShare: number;
  date: string;
}

export interface OrdersMap {
  [symbol: string]: Order[];
}

function generateId(): string {
  return 'p_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 6);
}

// --- Migration (runs once) ---
export function migrateIfNeeded(): void {
  if (localStorage.getItem(PORTFOLIOS_KEY)) return;

  const oldTickers = localStorage.getItem('portfolio_tickers');
  const oldOrders = localStorage.getItem('portfolio_orders');

  const defaultId = generateId();
  const portfolios: PortfolioInfo[] = [{ id: defaultId, name: 'Main Portfolio' }];

  localStorage.setItem(PORTFOLIOS_KEY, JSON.stringify(portfolios));
  localStorage.setItem(ACTIVE_PORTFOLIO_KEY, defaultId);

  if (oldTickers) {
    localStorage.setItem(`portfolio_tickers_${defaultId}`, oldTickers);
    localStorage.removeItem('portfolio_tickers');
  }
  if (oldOrders) {
    localStorage.setItem(`portfolio_orders_${defaultId}`, oldOrders);
    localStorage.removeItem('portfolio_orders');
  }
}

// --- Portfolio CRUD ---
export function getPortfolios(): PortfolioInfo[] {
  const raw = localStorage.getItem(PORTFOLIOS_KEY);
  return raw ? JSON.parse(raw) : [];
}

export function getActivePortfolioId(): string {
  return localStorage.getItem(ACTIVE_PORTFOLIO_KEY) || getPortfolios()[0]?.id || '';
}

export function setActivePortfolioId(id: string): void {
  localStorage.setItem(ACTIVE_PORTFOLIO_KEY, id);
}

export function createPortfolio(name: string): PortfolioInfo {
  const portfolios = getPortfolios();
  const newPortfolio: PortfolioInfo = { id: generateId(), name };
  portfolios.push(newPortfolio);
  localStorage.setItem(PORTFOLIOS_KEY, JSON.stringify(portfolios));
  return newPortfolio;
}

export function renamePortfolio(id: string, name: string): void {
  const portfolios = getPortfolios();
  const p = portfolios.find((x) => x.id === id);
  if (p) {
    p.name = name;
    localStorage.setItem(PORTFOLIOS_KEY, JSON.stringify(portfolios));
  }
}

export function deletePortfolio(id: string): void {
  let portfolios = getPortfolios();
  portfolios = portfolios.filter((p) => p.id !== id);
  localStorage.setItem(PORTFOLIOS_KEY, JSON.stringify(portfolios));
  localStorage.removeItem(`portfolio_tickers_${id}`);
  localStorage.removeItem(`portfolio_orders_${id}`);
  localStorage.removeItem(`portfolio_watchlist_${id}`);
  if (getActivePortfolioId() === id && portfolios.length > 0) {
    setActivePortfolioId(portfolios[0].id);
  }
}

// --- Scoped data access (defaults to active portfolio) ---
export function tickersKey(id?: string): string {
  return `portfolio_tickers_${id || getActivePortfolioId()}`;
}

export function ordersKey(id?: string): string {
  return `portfolio_orders_${id || getActivePortfolioId()}`;
}

export function getTickers(id?: string): string[] {
  const raw = localStorage.getItem(tickersKey(id));
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function saveTickers(tickers: string[], id?: string): void {
  localStorage.setItem(tickersKey(id), JSON.stringify(tickers));
}

export function getOrders(id?: string): OrdersMap {
  const raw = localStorage.getItem(ordersKey(id));
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return {};
}

export function saveOrders(orders: OrdersMap, id?: string): void {
  localStorage.setItem(ordersKey(id), JSON.stringify(orders));
  window.dispatchEvent(new CustomEvent('ordersUpdated'));
}

// --- Watchlist (scoped per portfolio) ---
export function watchlistKey(id?: string): string {
  return `portfolio_watchlist_${id || getActivePortfolioId()}`;
}

export function getWatchlist(id?: string): string[] {
  const raw = localStorage.getItem(watchlistKey(id));
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function saveWatchlist(symbols: string[], id?: string): void {
  localStorage.setItem(watchlistKey(id), JSON.stringify(symbols));
}

// --- Thesis Notes (scoped per portfolio + symbol) ---
export interface ThesisNote {
  rationale: string;
  catalyst: string;
  targetPrice: number | null;
  stopLoss: number | null;
  reviewDate: string;
  updatedAt: string;
}

function thesisKey(id?: string): string {
  return `portfolio_thesis_${id || getActivePortfolioId()}`;
}

export function getTheses(id?: string): Record<string, ThesisNote> {
  const raw = localStorage.getItem(thesisKey(id));
  if (raw) { try { return JSON.parse(raw); } catch { return {}; } }
  return {};
}

export function getThesis(symbol: string, id?: string): ThesisNote | null {
  return getTheses(id)[symbol] || null;
}

export function saveThesis(symbol: string, note: ThesisNote, id?: string): void {
  const all = getTheses(id);
  all[symbol] = { ...note, updatedAt: new Date().toISOString() };
  localStorage.setItem(thesisKey(id), JSON.stringify(all));
}

export function deleteThesis(symbol: string, id?: string): void {
  const all = getTheses(id);
  delete all[symbol];
  localStorage.setItem(thesisKey(id), JSON.stringify(all));
}
